import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiX, FiMessageCircle, FiMail, FiCopy, FiCheck } = FiIcons;

const ShareWithParent = ({ results, calculationMode, isPwdOnly }) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [relation, setRelation] = useState(isPwdOnly ? 'Friend/Family' : 'Mom/Dad');
  
  const relations = isPwdOnly 
    ? ['Friend', 'Family', 'Sibling', 'Colleague']
    : ['Mom', 'Dad', 'Mom/Dad', 'Lola', 'Lolo', 'Lola/Lolo', 'Nanay', 'Tatay'];

  const buildMessage = () => {
    if (!results) return '';
    const url = isPwdOnly ? 'https://seniordiscount.sulit.ph/pwd-discount-calculator/' : 'https://seniordiscount.sulit.ph';
    const originalPrice = parseFloat(results.originalPrice).toLocaleString('en-PH', { minimumFractionDigits: 2 });
    const discount = parseFloat(results.discount).toLocaleString('en-PH', { minimumFractionDigits: 2 });
    const finalAmount = parseFloat(results.billableAmount).toLocaleString('en-PH', { minimumFractionDigits: 2 });
    
    if (calculationMode === 'group') {
      const perPerson = parseFloat(results.pricePerPerson).toLocaleString('en-PH', { minimumFractionDigits: 2 });
      return `Hi ${relation}! 💛 You qualify for a ${isPwdOnly ? 'PWD' : 'Senior/PWD'} discount!\n\n📋 Bill: ₱${originalPrice}\n🎯 20% Discount: -₱${discount}\n✅ Your share: ₱${perPerson}\n\nUse this calculator next time you go out:\n${url}`;
    }
    
    return `Hi ${relation}! 💛 You qualify for a ${isPwdOnly ? 'PWD' : 'Senior/PWD'} discount!\n\n📋 Original Bill: ₱${originalPrice}\n🎯 20% Discount: -₱${discount}\n✅ Amount to Pay: ₱${finalAmount}\n\nUse this free calculator next time:\n${url}`;
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(buildMessage());
    window.open(`https://wa.me/?text=${msg}`, '_blank');
    track('whatsapp');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`${relation}, you qualify for a ${isPwdOnly ? 'PWD' : 'Senior/PWD'} discount!`);
    const body = encodeURIComponent(buildMessage());
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    track('email');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildMessage());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('copy');
    } catch {
      alert('Could not copy. Please copy manually.');
    }
  };

  const track = (method) => {
    if (window.gtag) {
      window.gtag('event', 'share_with_parent', {
        event_category: 'engagement',
        event_label: method,
        calculation_mode: calculationMode,
      });
    }
  };

  if (!results) return null;

  return (
    <>
      <motion.button 
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setShowModal(true)}
        className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 shadow-lg shadow-pink-200"
      >
        <SafeIcon icon={FiHeart} className="text-xl" />
        {isPwdOnly ? 'Share with Friend/Family' : 'Share with Parent'}
      </motion.button>

      <AnimatePresence>
        {showModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div 
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: 'spring', damping: 24, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-5 text-white relative">
                <button 
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 bg-white/20 rounded-full p-1.5"
                >
                  <SafeIcon icon={FiX} className="text-white text-sm" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">
                    💛
                  </div>
                  <div>
                    <h3 className="font-black text-lg leading-tight">{isPwdOnly ? 'Share Results' : 'Share with Parent'}</h3>
                    <p className="text-pink-100 text-xs font-medium">Let them know their discount!</p>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Relation Picker */}
                <div>
                  <p className="text-xs font-black text-slate-500 uppercase mb-2">Who are you sending to?</p>
                  <div className="flex flex-wrap gap-2">
                    {relations.map((r) => (
                      <button
                        key={r}
                        onClick={() => setRelation(r)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                          relation === r 
                          ? 'bg-pink-500 text-white border-pink-500' 
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message Preview */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-xs font-black text-slate-400 uppercase mb-2">Message Preview</p>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line font-medium">
                    {buildMessage()}
                  </p>
                </div>

                {/* Share Buttons */}
                <div className="space-y-2">
                  <motion.button 
                    whileTap={{ scale: 0.97 }}
                    onClick={handleWhatsApp}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3.5 rounded-2xl font-black flex items-center justify-center gap-3 transition-colors"
                  >
                    <SafeIcon icon={FiMessageCircle} className="text-lg" />
                    Send via WhatsApp
                  </motion.button>
                  <motion.button 
                    whileTap={{ scale: 0.97 }}
                    onClick={handleEmail}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3.5 rounded-2xl font-black flex items-center justify-center gap-3 transition-colors"
                  >
                    <SafeIcon icon={FiMail} className="text-lg" />
                    Send via Email
                  </motion.button>
                  <motion.button 
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCopy}
                    className={`w-full py-3.5 rounded-2xl font-black flex items-center justify-center gap-3 border-2 transition-all ${
                      copied 
                      ? 'bg-green-50 border-green-400 text-green-600' 
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-lg" />
                    {copied ? 'Message Copied!' : 'Copy Message'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShareWithParent;